import React, { useState, useEffect } from 'react';
import { Home, Heart, Users, Calendar, Clock, Award, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../components/PageTitle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Home: <Home className="w-12 h-12" />,
  Heart: <Heart className="w-12 h-12" />,
  Users: <Users className="w-12 h-12" />,
  Calendar: <Calendar className="w-8 h-8" />,
  Clock: <Clock className="w-8 h-8" />,
  Award: <Award className="w-8 h-8" />
};

const Services = () => {
  usePageTitle('Our Services | Lehigh Human Support Services');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default content (fallback)
  const defaultContent = {
    hero_title: 'Our Services',
    hero_subtitle: 'Comprehensive support services designed to help individuals with intellectual and developmental disabilities live fulfilling, independent lives',
    main_services: [
      {
        icon: 'Home',
        title: 'Residential Support Services',
        description: 'We provide comprehensive residential support to help individuals with intellectual and developmental disabilities live independently in their communities.',
        features: [
          '24/7 on-call support and emergency assistance',
          'Assistance with daily living activities',
          'Medication management and health monitoring',
          'Household management and safety training',
          'Transportation coordination',
          'Community integration activities'
        ],
        image: 'https://images.unsplash.com/photo-1713362758789-2530a5fa724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxkaXNhYmlsaXR5JTIwc3VwcG9ydHxlbnwwfHx8fDE3Njg4MDY5NjF8MA&ixlib=rb-4.1.0&q=85'
      },
      {
        icon: 'Heart',
        title: 'Personal Care & Daily Living Support',
        description: 'Our Direct Support Professionals provide compassionate, person-centered care tailored to each individual\'s unique needs and goals.',
        features: [
          'Personal hygiene and grooming assistance',
          'Meal planning and nutrition support',
          'Money management and budgeting skills',
          'Social skills development',
          'Goal setting and achievement planning',
          'Emotional and behavioral support'
        ],
        image: 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwY2FyZWdpdmVyc3xlbnwwfHx8fDE3Njg4MDY5NjV8MA&ixlib=rb-4.1.0&q=85'
      },
      {
        icon: 'Users',
        title: 'Community Integration Programs',
        description: 'We facilitate meaningful community participation and social connections through structured programs and activities.',
        features: [
          'Recreational and leisure activities',
          'Volunteer and employment opportunities',
          'Educational programs and skill-building workshops',
          'Social events and group outings',
          'Family engagement and support',
          'Advocacy and self-determination training'
        ],
        image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc2ODgwNjk3MXww&ixlib=rb-4.1.0&q=85'
      }
    ],
    additional_services: [
      {
        icon: 'Calendar',
        title: 'Program Coordination',
        description: 'Our experienced coordinators ensure seamless service delivery and comprehensive care planning.'
      },
      {
        icon: 'Clock',
        title: '24/7 Support',
        description: 'Round-the-clock availability for emergencies and ongoing support needs.'
      },
      {
        icon: 'Award',
        title: 'Quality Assurance',
        description: 'Continuous monitoring and improvement of services to maintain the highest standards of care.'
      }
    ],
    process_title: 'How We Work',
    process_subtitle: 'Our person-centered approach ensures individualized care and support',
    process_steps: [
      { step: '01', title: 'Initial Assessment', description: 'We conduct a thorough assessment to understand individual needs, preferences, and goals.' },
      { step: '02', title: 'Personalized Planning', description: 'We develop a customized support plan tailored to each person\'s unique circumstances.' },
      { step: '03', title: 'Service Implementation', description: 'Our trained professionals deliver high-quality, consistent support services.' },
      { step: '04', title: 'Ongoing Review', description: 'We regularly review and adjust services to ensure continued effectiveness and satisfaction.' }
    ],
    cta_title: 'Ready to Learn More?',
    cta_subtitle: 'Contact us today to discuss how our services can support you or your loved one.',
    cta_button_text: 'Get in Touch',
    cta_button_link: '/contact'
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/services`);
      const merged = { ...defaultContent, ...response.data };
      setContent(merged);
    } catch (error) {
      console.error('Error fetching services content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { main_services, additional_services, process_steps } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {content.hero_title}
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 space-y-24">
          {main_services.map((service, index) => (
            <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full text-amber-600">
                  {iconMap[service.icon] || <Home className="w-12 h-12" />}
                </div>
                <h2 className="text-4xl font-bold text-gray-900">{service.title}</h2>
                <p className="text-lg text-gray-700 leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {(service.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.additional_title || 'Additional Support'}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.additional_subtitle || 'Beyond our core services, we provide comprehensive support coordination and quality assurance'}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {additional_services.map((service, index) => (
              <Card key={index} className="border-2 border-amber-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8 space-y-4 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-full text-white">
                    {iconMap[service.icon] || <Calendar className="w-8 h-8" />}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.process_title || 'How We Work'}</h2>
              <p className="text-xl text-gray-600">
                {content.process_subtitle || 'Our person-centered approach ensures individualized care and support'}
              </p>
            </div>
            <div className="space-y-8">
              {process_steps.map((item, index) => (
                <div key={index} className="flex gap-6 items-start group">
                  <div className="flex-shrink-0 w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                    {item.step}
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                    <p className="text-lg text-gray-700 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            {content.cta_title || 'Ready to Learn More?'}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {content.cta_subtitle || 'Contact us today to discuss how our services can support you or your loved one.'}
          </p>
          <Link to={content.cta_button_link || '/contact'}>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              {content.cta_button_text || 'Get in Touch'}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;